/**
 * First B2B quiz — recruiter AI-usage segmentation + ICP validation.
 * Reached at /quiz/url-quiz. See _docs/superpowers/specs/2026-08-12-b2b-quiz-design.md.
 */

// Q5-Q7 only make sense for respondents who have actually used an AI tool —
// "No, but actively looking" and "No, not interested" have no real answer to
// give for "which tasks/tools" or "how much time did it save". Skip only on
// an explicit disqualifying answer, not on "not answered yet" — Q4 comes
// right before Q5 in the array, so while the respondent is still on Q1-Q4,
// `ai_usage_status` is undefined and Q5-Q7 must stay counted/visible until
// Q4 is actually answered one way or the other.
const AI_NON_USER_STATUSES = ['no_looking', 'no_not_interested'];
const skipsAiUsageQuestions = (answers) => AI_NON_USER_STATUSES.includes(answers.ai_usage_status?.value);

export const URL_QUIZ = {
  slug: 'url-quiz',
  status: 'active',
  title: 'Recruiter AI Screening Survey',
  intro:
    'Before you start: by "AI tool" we mean any AI-based feature you use to screen or evaluate candidates — whether built into your ATS, a standalone tool, or general-purpose AI like ChatGPT/Claude used for this task.',
  questions: [
    {
      key: 'role',
      block: 'Segmentation',
      type: 'single',
      question: 'What best describes your role?',
      options: [
        { value: 'agency_recruiter', label: 'Agency recruiter (staffing / RPO)' },
        { value: 'inhouse_recruiter', label: 'In-house / corporate recruiter or TA' },
        { value: 'hiring_manager', label: 'Hiring manager' },
        { value: 'hr_generalist', label: 'HR generalist' },
        { value: 'other', label: 'Other' },
      ],
      allowOther: true,
    },
    {
      key: 'screen_volume',
      block: 'Segmentation',
      type: 'single',
      question: 'How many candidates do you personally screen per week (resume review + initial evaluation)?',
      options: [
        { value: 'under_10', label: 'Under 10' },
        { value: '10_29', label: '10–29' },
        { value: '30_74', label: '30–74' },
        { value: '75_plus', label: '75+' },
      ],
    },
    {
      key: 'recruit_region',
      block: 'Segmentation',
      type: 'single',
      question: 'Where do you primarily recruit for?',
      options: [
        { value: 'usa', label: 'USA' },
        { value: 'uk', label: 'UK' },
        { value: 'canada', label: 'Canada' },
        { value: 'australia', label: 'Australia' },
        { value: 'eu_europe', label: 'EU or other Europe' },
        { value: 'other', label: 'Other' },
      ],
      allowOther: true,
    },
    {
      key: 'ai_usage_status',
      block: 'Current AI usage',
      type: 'single',
      question: 'Do you currently use any AI tool as part of your screening process?',
      options: [
        { value: 'yes_regularly', label: 'Yes, regularly' },
        { value: 'tried_stopped', label: 'Tried it, but stopped' },
        { value: 'no_looking', label: 'No, but actively looking' },
        { value: 'no_not_interested', label: 'No, not interested' },
      ],
    },
    {
      key: 'ai_tasks_used',
      block: 'Current AI usage',
      type: 'multi',
      question: 'Which tasks do you use or have tried AI for?',
      options: [
        { value: 'parsing_matching', label: 'Parsing/matching resumes to job requirements' },
        { value: 'ranking_scoring', label: 'Ranking or scoring candidates' },
        { value: 'summarizing', label: 'Summarizing candidate profiles' },
        { value: 'screening_chatbot', label: 'Initial screening questions / chatbot pre-screen' },
        { value: 'video_interview_analysis', label: 'Analyzing video or async interview responses' },
        { value: 'outreach_messages', label: 'Writing outreach/sourcing messages' },
        { value: 'scheduling', label: 'Scheduling' },
        { value: 'other', label: 'Other' },
      ],
      allowOther: true,
      skipIf: (answers) => skipsAiUsageQuestions(answers),
    },
    {
      key: 'ai_tools_used',
      block: 'Current AI usage',
      type: 'multi',
      question: 'Which tools or platforms do you use for this?',
      options: [
        { value: 'ats_builtin', label: 'Built into my ATS (Bullhorn, Greenhouse, etc.)' },
        { value: 'general_purpose_ai', label: 'General-purpose AI (ChatGPT, Claude, etc.)' },
        { value: 'dedicated_ai_tool', label: 'Dedicated AI screening tool (HireVue, Sapia.ai, etc.)' },
        { value: 'custom_inhouse', label: 'Custom/in-house built' },
        { value: 'none', label: "I don't use any tool currently" },
        { value: 'other', label: 'Other' },
      ],
      allowOther: true,
      skipIf: (answers) => skipsAiUsageQuestions(answers),
    },
    {
      key: 'time_saved',
      block: 'Time & budget',
      type: 'single',
      question: 'Roughly how much time does AI save you per week on screening?',
      options: [
        { value: 'none', label: 'None — no noticeable time saved' },
        { value: 'less_1h', label: 'Less than 1 hour' },
        { value: '1_4h', label: '1–4 hours' },
        { value: '5_9h', label: '5–9 hours' },
        { value: '10h_plus', label: '10+ hours' },
      ],
      skipIf: (answers) => skipsAiUsageQuestions(answers),
    },
    {
      key: 'monthly_budget',
      block: 'Time & budget',
      type: 'single',
      question: "What's your current monthly budget for recruiting/screening software (per seat)?",
      options: [
        { value: '0_not_budgeted', label: "$0 — haven't budgeted" },
        { value: 'under_50', label: 'Under $50' },
        { value: '50_149', label: '$50–149' },
        { value: '150_349', label: '$150–349' },
        { value: '350_plus', label: '$350+' },
        { value: 'not_sure', label: 'Not sure' },
      ],
    },
    {
      key: 'buyer',
      block: 'Time & budget',
      type: 'single',
      question: 'Who decides which tools get purchased at your organization?',
      options: [
        { value: 'self', label: 'I decide myself' },
        { value: 'manager', label: 'My manager or team lead' },
        { value: 'procurement', label: 'Procurement/finance' },
        { value: 'owner_founder', label: 'Owner/founder' },
        { value: 'other', label: 'Other' },
      ],
      allowOther: true,
    },
    {
      key: 'concerns',
      block: 'Trust & blockers',
      type: 'multi',
      question: "What's your biggest concern about using AI in candidate screening? (pick up to 2)",
      options: [
        { value: 'accuracy', label: 'Accuracy — risk of missing good candidates' },
        { value: 'bias_fairness', label: 'Bias / fairness' },
        { value: 'candidate_experience', label: 'Candidate experience (feels impersonal)' },
        { value: 'compliance_legal', label: 'Compliance / legal risk (EU AI Act, US state laws, etc.)' },
        { value: 'cost', label: 'Cost' },
        { value: 'dont_trust_ai', label: "Don't trust AI judgment on people" },
        { value: 'integration_effort', label: 'Too much effort to learn/integrate' },
        { value: 'other', label: 'Other' },
      ],
      allowOther: true,
      maxSelect: 2,
    },
    {
      key: 'trust_builders',
      block: 'Trust & blockers',
      type: 'multi',
      question: 'What would make you trust an AI screening tool more?',
      options: [
        { value: 'transparent_scoring', label: 'Transparent, explainable scoring criteria' },
        { value: 'human_review_step', label: 'A human review step before the final decision' },
        { value: 'case_studies', label: 'Case studies from similar agencies' },
        { value: 'free_trial', label: 'Free trial before committing' },
        { value: 'compliance_cert', label: 'Compliance certification / audit trail' },
        { value: 'other', label: 'Other' },
      ],
      allowOther: true,
    },
    {
      key: 'tedious_part',
      block: 'Trust & blockers',
      type: 'text',
      question: "What's the most tedious part of your screening process today that you wish AI could fix?",
      required: false,
    },
  ],
};
